import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { DsoTrendItem } from '../../services/apis/dashboard/dashboard.service';
import { ThemeService } from '../../services/theme.service';
import { chartColors } from './chart-theme';

@Component({
  selector: 'app-dso-trend-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './dso-trend-chart.component.html',
})
export class DsoTrendChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: DsoTrendItem[] = [];
  @Input() targetDays = 45;

  options: EChartsOption | null = null;
  readonly viewportSize = 12;
  viewportStart = 0;

  @ViewChild('chartContainer') private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private dataReference: DsoTrendItem[] | null = null;
  private lastWheelTime = 0;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) this.buildOptions();
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data !== this.dataReference) {
      this.dataReference = this.data;
      this.viewportStart = 0;
    }
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    this.setupWheelAndResize();
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.chartContainer?.nativeElement) {
      this.chartContainer.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
    }
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
    this.setupWheelAndResize();
  }

  get hasScrollableViewport(): boolean {
    return (this.data?.length ?? 0) > this.viewportSize;
  }

  get canMoveOlder(): boolean {
    return this.viewportStart > 0;
  }

  get canMoveNewer(): boolean {
    return this.viewportStart < Math.max(0, (this.data?.length ?? 0) - this.viewportSize);
  }

  get viewportLabel(): string {
    const rows = this.visibleRows();
    return rows.length
      ? `${rows[0].monthLabel || rows[0].month} – ${rows[rows.length - 1].monthLabel || rows[rows.length - 1].month}`
      : '';
  }

  moveViewport(direction: 'older' | 'newer'): void {
    const max = Math.max(0, (this.data?.length ?? 0) - this.viewportSize);
    const next = Math.max(0, Math.min(max, this.viewportStart + (direction === 'newer' ? 1 : -1)));
    if (next === this.viewportStart) return;
    this.viewportStart = next;
    this.buildOptions();
  }

  onWheel(event: WheelEvent): void {
    if (!this.hasScrollableViewport) return;
    event.preventDefault();
    event.stopPropagation();
    const now = Date.now();
    if (now - this.lastWheelTime < 120) return;
    this.lastWheelTime = now;
    this.moveViewport(event.deltaY > 0 ? 'newer' : 'older');
  }

  private readonly onNativeWheel = (event: WheelEvent): void => {
    this.onWheel(event);
  };

  private setupWheelAndResize(): void {
    if (this.chartContainer?.nativeElement) {
      this.chartContainer.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
      this.chartContainer.nativeElement.addEventListener('wheel', this.onNativeWheel, { passive: false, capture: true });
      if (!this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
        this.resizeObserver.observe(this.chartContainer.nativeElement);
      }
    }
  }

  private visibleRows(): DsoTrendItem[] {
    const rows = this.data ?? [];
    return this.hasScrollableViewport
      ? rows.slice(this.viewportStart, this.viewportStart + this.viewportSize)
      : rows;
  }

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const rows = this.visibleRows();
    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const c = chartColors();
    const color = c.accent || c.chartSeries1;

    this.options = {
      grid: { left: 34, right: 12, top: 12, bottom: 52, containLabel: true },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'line', lineStyle: { color: c.cardBorder, type: 'dashed' } },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        valueFormatter: value => `${Number(value).toFixed(2)} days`,
      },
      legend: {
        bottom: 0,
        icon: 'circle',
        data: ['DSO', `${this.targetDays} day target`],
        textStyle: { color: c.textSecondary, fontSize: 11, fontWeight: 500 },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: rows.map(row => row.monthLabel || row.month),
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 10,
          margin: 12,
          interval: 0,
          rotate: 32,
          hideOverlap: false,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: c.textSecondary, fontSize: 11, formatter: (value: number) => `${value}d` },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.75 } },
      },
      series: [
        {
          name: 'DSO',
          type: rows.length === 1 ? 'bar' : 'line',
          data: rows.map(row => (Number.isFinite(row.dso as number) ? (row.dso as number) : 0)),
          barMaxWidth: 56,
          smooth: 0.2,
          symbol: 'circle',
          symbolSize: rows.length <= 4 ? 9 : 6,
          itemStyle: { color, borderRadius: rows.length === 1 ? [5, 5, 0, 0] : 0 },
          lineStyle: { color, width: 2.8 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: this.toRgba(color, 0.22) },
                { offset: 1, color: this.toRgba(color, 0) },
              ],
            },
          },
        },
        {
          name: `${this.targetDays} day target`,
          type: 'line',
          data: rows.map(() => this.targetDays),
          symbol: 'none',
          itemStyle: { color: '#F04438' },
          lineStyle: { color: '#F04438', width: 2, type: 'dashed' },
          emphasis: { disabled: true },
        },
      ],
    };
  }

  private toRgba(color: string, alpha: number): string {
    const hex = color.trim();
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return color;
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
